class ProductUpsell extends HTMLElement {
    constructor() {
      super();
      this.selectedItems = new Set();
      this.totalPrice = 0;
      this.cartDrawer = null;
    }
  
    connectedCallback() {
      this.cartDrawer = document.querySelector('cart-drawer');
      this.initializeItems();
      this.bindEvents();
      this.updateSummary();
    }
  
    initializeItems() {
      const items = this.querySelectorAll('product-upsell-item');
      items.forEach(item => {
        if (!customElements.get('product-upsell-item')) {
          customElements.define('product-upsell-item', ProductUpsellItem);
        }
      });
    }
  
    bindEvents() {
      this.addEventListener('upsell:item-selected', this.handleItemSelection.bind(this));
      this.addEventListener('upsell:item-deselected', this.handleItemDeselection.bind(this));
      this.addEventListener('upsell:price-changed', this.handlePriceChange.bind(this));
  
      const addButton = this.querySelector('[data-add-selected]');
      if (addButton) {
        addButton.addEventListener('click', this.handleAddToCart.bind(this));
      }
    }
  
    handleItemSelection(event) {
      const { productId, price } = event.detail;
      this.selectedItems.add(productId);
      this.updateSummary();
    }
  
    handleItemDeselection(event) {
      const { productId } = event.detail;
      this.selectedItems.delete(productId);
      this.updateSummary();
    }
  
    handlePriceChange(event) {
      this.updateSummary();
    }
  
    updateSummary() {
      const totalPriceElement = this.querySelector('[data-total-price]');
      const addButton = this.querySelector('[data-add-selected]');
      const buttonText = addButton?.querySelector('[data-button-text]');
  
      // Calculate total price from selected items
      this.totalPrice = 0;
      const selectedItemElements = Array.from(this.querySelectorAll('product-upsell-item'))
        .filter(item => {
          const checkbox = item.querySelector('[data-product-checkbox]');
          return checkbox && checkbox.checked;
        });
  
      selectedItemElements.forEach(item => {
        const price = item.getCurrentPrice();
        this.totalPrice += price;
      });
  
      // Update total price display
      if (totalPriceElement) {
        totalPriceElement.textContent = this.formatMoney(this.totalPrice);
      }
  
      // Update button state and text
      const selectedCount = selectedItemElements.length;
      if (addButton) {
        addButton.disabled = selectedCount === 0;
        
        if (buttonText) {
          if (selectedCount === 0) {
            buttonText.textContent = this.getTranslation('add_items_to_cart', { count: 0 });
          } else {
            buttonText.textContent = this.getTranslation('add_items_to_cart', { count: selectedCount });
          }
        }
      }
    }
  
    async handleAddToCart() {
      const addButton = this.querySelector('[data-add-selected]');
      const loadingOverlay = addButton?.querySelector('.loading-overlay');
      
      if (!addButton || addButton.disabled) return;
  
      // Get selected items
      const selectedItems = Array.from(this.querySelectorAll('product-upsell-item'))
        .filter(item => {
          const checkbox = item.querySelector('[data-product-checkbox]');
          return checkbox && checkbox.checked;
        });
  
      if (selectedItems.length === 0) return;
  
      // Show loading state
      addButton.disabled = true;
      loadingOverlay?.classList.remove('hidden');
  
      try {
        // Prepare form data for cart add
        const formData = new FormData();
        
        selectedItems.forEach((item, index) => {
          const variantId = item.getCurrentVariantId();
          formData.append(`items[${index}][id]`, variantId);
          formData.append(`items[${index}][quantity]`, '1');
        });

        // Add sections to update if cart drawer exists
        if (this.cartDrawer) {
          const sections = this.cartDrawer.getSectionsToRender?.()?.map(section => section.section);
          if (sections) {
            formData.append('sections', sections.join(','));
          }
          formData.append('sections_url', window.location.pathname);
        }

        // Add items to cart
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add items to cart');
        }

        const result = await response.json();

        // Update cart drawer if available
        if (this.cartDrawer && result.sections) {
          this.cartDrawer.renderContents(result);
          this.cartDrawer.open();
        }

        // Reset selections
        this.resetSelections();

        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { 
          detail: { items: result.items || [] }
        }));

        // Dispatch success event
        this.dispatchEvent(new CustomEvent('upsell:add-success', {
          detail: { items: result.items },
          bubbles: true
        }));

      } catch (error) {
        console.error('Error adding items to cart:', error);
        
        // Dispatch error event
        this.dispatchEvent(new CustomEvent('upsell:add-error', {
          detail: { error: error.message },
          bubbles: true
        }));
      } finally {
        // Hide loading state
        loadingOverlay?.classList.add('hidden');
        addButton.disabled = false;
      }
    }
  
    resetSelections() {
      const checkboxes = this.querySelectorAll('[data-product-checkbox]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      this.selectedItems.clear();
      this.updateSummary();
    }
  
    formatMoney(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }
  
    getTranslation(key, options = {}) {
      // Simple translation helper - in a real implementation, you'd use Shopify's translation system
      const translations = {
        'add_items_to_cart': options.count === 1 ? 'ADD 1 ITEM TO CART' : `ADD ${options.count} ITEMS TO CART`
      };
      return translations[key] || key;
    }
  }
  
  class ProductUpsellItem extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.currentVariant = null;
      this.checkbox = null;
    }
  
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      this.currentVariant = this.variants[0];
      this.checkbox = this.querySelector('[data-product-checkbox]');
      
      this.initVariantSelection();
      this.initVariantAvailability(); // Add this line
      this.bindEvents();
      this.updateVariant();
    }
  
    initVariantSelection() {
      const selects = this.querySelectorAll('.product-upsell__variant-select');
      selects.forEach(select => {
        select.addEventListener('change', this.onVariantChange.bind(this));
      });
    }
  
    bindEvents() {
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
      }
    }
  
    onCheckboxChange() {
      const isSelected = this.checkbox.checked;
      const productId = this.dataset.productId;
      const price = this.getCurrentPrice();
  
      if (isSelected) {
        this.dispatchEvent(new CustomEvent('upsell:item-selected', {
          detail: { productId, price },
          bubbles: true
        }));
      } else {
        this.dispatchEvent(new CustomEvent('upsell:item-deselected', {
          detail: { productId },
          bubbles: true
        }));
      }
    }
  
    onVariantChange() {
      this.updateVariant();
      
      // Dispatch price change event if item is selected
      if (this.checkbox && this.checkbox.checked) {
        this.dispatchEvent(new CustomEvent('upsell:price-changed', {
          detail: { 
            productId: this.dataset.productId,
            price: this.getCurrentPrice()
          },
          bubbles: true
        }));
      }
    }
  
    updateVariant() {
      const selectedOptions = this.getSelectedOptions();
      this.currentVariant = this.findMatchingVariant(selectedOptions);
      
      if (this.currentVariant) {
        this.updateVariantId();
        this.updatePrice();
        this.updateImage();
        this.updateAvailability();
      }
    }
  
    getSelectedOptions() {
      const selects = this.querySelectorAll('.product-upsell__variant-select');
      return Array.from(selects).map(select => select.value);
    }
  
    findMatchingVariant(selectedOptions) {
      return this.variants.find(variant => {
        return selectedOptions.every((option, index) => {
          return variant[`option${index + 1}`] === option;
        });
      }) || this.variants[0];
    }
  
    updateVariantId() {
      const variantIdInput = this.querySelector('[data-variant-id]');
      if (variantIdInput && this.currentVariant) {
        variantIdInput.value = this.currentVariant.id;
      }
    }
  
    updatePrice() {
      const priceDisplay = this.querySelector('[data-price-display]');
      if (!priceDisplay || !this.currentVariant) return;
  
      const price = this.currentVariant.price;
      const compareAtPrice = this.currentVariant.compare_at_price;
  
      if (compareAtPrice && compareAtPrice > price) {
        priceDisplay.innerHTML = `
          <span class="visually-hidden">Regular price</span>
          <del class="product-upsell__price-compare">${this.formatMoney(compareAtPrice)}</del>
          <span class="visually-hidden">Sale price</span>
          <ins class="product-upsell__price-current">${this.formatMoney(price)}</ins>
        `;
      } else {
        priceDisplay.innerHTML = `
          <span class="visually-hidden">Price</span>
          <span class="product-upsell__price-current">${this.formatMoney(price)}</span>
        `;
      }
    }
  
    updateAvailability() {
      const checkbox = this.checkbox;
      if (!checkbox || !this.currentVariant) return;

      if (this.currentVariant.available) {
        checkbox.disabled = false;
        this.classList.remove('unavailable');
      } else {
        checkbox.disabled = true;
        checkbox.checked = false;
        this.classList.add('unavailable');
        
        // Trigger deselection event if it was selected
        this.onCheckboxChange();
      }
    }
  
    getCurrentPrice() {
      return this.currentVariant ? this.currentVariant.price : 0;
    }
  
    getCurrentVariantId() {
      return this.currentVariant ? this.currentVariant.id : null;
    }
  
    formatMoney(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }

    updateImage() {
      if (!this.currentVariant) return;

      const mediaElement = this.querySelector('.product-upsell__item-media .media');
      const imageElement = mediaElement?.querySelector('img');
      
      if (!imageElement) return;

      // Use variant image if available, otherwise keep the product's featured image
      const variantImage = this.currentVariant.featured_image;
      
      // Check for 'src' property instead of 'url'
      if (!variantImage || !variantImage.src) return;

      // Use 'src' property and ensure it has protocol
      let newImageUrl = variantImage.src;
      if (newImageUrl.startsWith('//')) {
        newImageUrl = 'https:' + newImageUrl;
      }
      
      const newImageAlt = variantImage.alt || this.currentVariant.title || '';

      // Handle both lazy-loaded and native images
      if (imageElement.classList.contains('lazyload')) {
        // For lazy-loaded images, update data-src
        imageElement.setAttribute('data-src', newImageUrl);
        imageElement.setAttribute('alt', newImageAlt);
        
        // If the image has already been loaded, update src as well
        if (imageElement.src && imageElement.src !== '') {
          imageElement.src = newImageUrl;
        }
        
        // Trigger lazy loading update if using a lazy loading library
        if (window.lazySizes) {
          window.lazySizes.loader.unveil(imageElement);
        }
      } else {
        // For native images, update src directly
        imageElement.src = newImageUrl;
        imageElement.setAttribute('alt', newImageAlt);
      }

      // Update srcset if present for responsive images
      if (variantImage.width && variantImage.src) {
        const newSrcset = this.generateSrcset(variantImage);
        if (newSrcset && imageElement.hasAttribute('srcset')) {
          imageElement.setAttribute('srcset', newSrcset);
        }
        if (newSrcset && imageElement.hasAttribute('data-srcset')) {
          imageElement.setAttribute('data-srcset', newSrcset);
        }
      }
    }

    generateSrcset(image) {
      // Add safety checks - use 'src' instead of 'url'
      if (!image || !image.src || !image.width) {
        return '';
      }

      // Generate srcset for responsive images based on Shopify's image transformation
      const widths = [180, 360, 480, 720, 1080];
      const srcsetParts = [];
      
      try {
        let baseUrl = image.src;
        // Ensure protocol is present
        if (baseUrl.startsWith('//')) {
          baseUrl = 'https:' + baseUrl;
        }

        for (const width of widths) {
          if (width <= image.width) {
            // Use Shopify's image transformation URL format
            const transformedUrl = baseUrl.replace(/(\?v=\d+)?(&width=\d+)?$/i, '') + `?v=${Date.now()}&width=${width}`;
            srcsetParts.push(`${transformedUrl} ${width}w`);
          }
        }
        
        // Add the original image as the largest option
        if (srcsetParts.length > 0) {
          srcsetParts.push(`${baseUrl} ${image.width}w`);
        }
        
        return srcsetParts.join(', ');
      } catch (error) {
        return '';
      }
    }

    initVariantAvailability() {
      const selects = this.querySelectorAll('.product-upsell__variant-select');
      
      selects.forEach((select, selectIndex) => {
        const optionIndex = select.dataset.optionIndex ? 
          parseInt(select.dataset.optionIndex) - 1 : 
          selectIndex;
        
        Array.from(select.options).forEach(option => {
          const optionValue = option.value;
          
          // Find variants that match this option value
          const matchingVariants = this.variants.filter(variant => {
            return variant[`option${optionIndex + 1}`] === optionValue;
          });
          
          // Check if any matching variant is available
          const isAvailable = matchingVariants.some(variant => variant.available);
          
          // Update option availability and text
          if (!isAvailable) {
            option.disabled = true;
            // Add text indicator since we can't style options
            if (!option.textContent.includes('(Out of Stock)')) {
              option.textContent = `${optionValue} (Out of Stock)`;
            }
          } else {
            option.disabled = false;
            // Ensure clean text for available options
            option.textContent = optionValue;
          }
        });
      });
    }
  }
  
  // Register custom elements
  if (!customElements.get('product-upsell')) {
    customElements.define('product-upsell', ProductUpsell);
  }
  
  if (!customElements.get('product-upsell-item')) {
    customElements.define('product-upsell-item', ProductUpsellItem);
  }